'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import ServiceCard from './ServiceCard';
import ServiceComparison from './ServiceComparison';
import CoverageMap from './CoverageMap';
import TestimonialCard from './TestimonialCard';
import PricingCalculator from './PricingCalculator';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface Service {
  id: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  price: string;
  features: Array<{icon: string;text: string;}>;
  popular?: boolean;
}

interface Testimonial {
  name: string;
  location: string;
  image: string;
  alt: string;
  rating: number;
  service: string;
  testimonial: string;
  date: string;
}

const ServicesInteractive = () => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<string>('all');
  const { language, t } = useLanguage();

  const services: Service[] = [
  {
    id: 'residential',
    title: t({ en: 'Residential Moving', ar: 'نقل سكني' }),
    description: t({
      en: 'Complete home moving solutions for apartments, villas, and houses across Kuwait with professional care and attention to every detail.',
      ar: 'حلول نقل منزلية كاملة للشقق والفلل والمنازل في جميع أنحاء الكويت مع رعاية احترافية واهتمام بكل التفاصيل.'
    }),
    image: "https://images.unsplash.com/photo-1588618370256-556289a24e96",
    alt: t({ en: 'Professional movers carefully loading furniture into moving truck with protective blankets', ar: 'عمال نقل محترفون يقومون بتحميل الأثاث بعناية في شاحنة النقل مع بطانيات واقية' }),
    price: '50',
    popular: true,
    features: [
    { icon: 'HomeIcon', text: t({ en: 'Door-to-door service', ar: 'خدمة من الباب إلى الباب' }) },
    { icon: 'ShieldCheckIcon', text: t({ en: 'Full insurance coverage', ar: 'تغطية تأمينية كاملة' }) },
    { icon: 'TruckIcon', text: t({ en: 'Professional packing materials', ar: 'مواد تعبئة احترافية' }) },
    { icon: 'ClockIcon', text: t({ en: 'Flexible scheduling', ar: 'جدولة مرنة' }) },
    { icon: 'UserGroupIcon', text: t({ en: 'Experienced moving team', ar: 'فريق نقل ذو خبرة' }) }]

  },
  {
    id: 'commercial',
    title: t({ en: 'Commercial Relocation', ar: 'نقل تجاري' }),
    description: t({
      en: 'Minimize downtime with our specialized commercial moving services designed for offices, retail spaces, and businesses of all sizes.',
      ar: 'قلل من وقت التوقف مع خدمات النقل التجاري المتخصصة المصممة للمكاتب ومساحات البيع بالتجزئة والشركات من جميع الأحجام.'
    }),
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12574467f-1766845773148.png",
    alt: t({ en: 'Modern office space with desks and computers being prepared for commercial relocation', ar: 'مساحة مكتبية حديثة مع مكاتب وأجهزة كمبيوتر يتم إعدادها للنقل التجاري' }),
    price: '80',
    features: [
    { icon: 'BuildingOfficeIcon', text: t({ en: 'After-hours moving available', ar: 'النقل متاح بعد ساعات العمل' }) },
    { icon: 'ComputerDesktopIcon', text: t({ en: 'IT equipment handling', ar: 'التعامل مع معدات تكنولوجيا المعلومات' }) },
    { icon: 'DocumentTextIcon', text: t({ en: 'Detailed inventory management', ar: 'إدارة مخزون مفصلة' }) },
    { icon: 'CalendarIcon', text: t({ en: 'Project coordination', ar: 'تنسيق المشروع' }) },
    { icon: 'ShieldCheckIcon', text: t({ en: 'Business insurance coverage', ar: 'تغطية تأمين الأعمال' }) }]

  },
  {
    id: 'packing',
    title: t({ en: 'Professional Packing', ar: 'تعبئة احترافية' }),
    description: t({
      en: 'Expert packing services using premium materials to ensure your belongings are protected throughout the moving process.',
      ar: 'خدمات تعبئة خبيرة باستخدام مواد عالية الجودة لضمان حماية ممتلكاتك طوال عملية النقل.'
    }),
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16885cae6-1767260679136.png",
    alt: t({ en: 'Professional packer wrapping delicate items in bubble wrap and placing in labeled cardboard boxes', ar: 'عامل تعبئة محترف يلف العناصر الحساسة في غلاف فقاعي ويضعها في صناديق كرتون مُعلَّمة' }),
    price: '30',
    features: [
    { icon: 'ArchiveBoxIcon', text: t({ en: 'Quality packing materials', ar: 'مواد تعبئة عالية الجودة' }) },
    { icon: 'SparklesIcon', text: t({ en: 'Fragile item specialists', ar: 'متخصصون في العناصر الهشة' }) },
    { icon: 'TagIcon', text: t({ en: 'Labeling and organization', ar: 'وضع العلامات والتنظيم' }) },
    { icon: 'CubeIcon', text: t({ en: 'Custom crating for valuables', ar: 'صناديق مخصصة للأشياء الثمينة' }) },
    { icon: 'ClipboardDocumentCheckIcon', text: t({ en: 'Unpacking service available', ar: 'خدمة فك التعبئة متاحة' }) }]

  },
  {
    id: 'assembly',
    title: t({ en: 'Furniture Assembly', ar: 'تجميع الأثاث' }),
    description: t({
      en: 'Professional furniture disassembly, transport, and reassembly services to ensure your furniture arrives intact and ready to use.',
      ar: 'خدمات فك وتجميع ونقل الأثاث الاحترافية لضمان وصول أثاثك سليمًا وجاهزًا للاستخدام.'
    }),
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10c964248-1766527718111.png",
    alt: t({ en: 'Skilled technician assembling modern furniture with tools in bright living room', ar: 'فني ماهر يقوم بتجميع الأثاث الحديث بالأدوات في غرفة معيشة مشرقة' }),
    price: '25',
    features: [
    { icon: 'WrenchScrewdriverIcon', text: t({ en: 'Expert assembly team', ar: 'فريق تجميع خبير' }) },
    { icon: 'CubeTransparentIcon', text: t({ en: 'All furniture types', ar: 'جميع أنواع الأثاث' }) },
    { icon: 'ShieldCheckIcon', text: t({ en: 'Assembly guarantee', ar: 'ضمان التجميع' }) },
    { icon: 'HomeModernIcon', text: t({ en: 'Placement assistance', ar: 'مساعدة في الوضع' }) },
    { icon: 'CheckCircleIcon', text: t({ en: 'Quality inspection', ar: 'فحص الجودة' }) }]

  }];


  const testimonials: Testimonial[] = [
  {
    name: t({ en: 'Sarah Al-Mutairi', ar: 'سارة المطيري' }),
    location: t({ en: 'Farwaniya, Kuwait', ar: 'الفروانية، الكويت' }),
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5f955b0-1763294351669.png",
    alt: t({ en: 'Professional Kuwaiti woman with long dark hair smiling warmly at camera', ar: 'امرأة كويتية محترفة ذات شعر داكن طويل تبتسم بدفء للكاميرا' }),
    rating: 5,
    service: t({ en: 'Residential Moving', ar: 'نقل سكني' }),
    testimonial: t({
      en: 'Almanara made our villa move completely stress-free. The team was professional, careful with our belongings, and finished ahead of schedule. Highly recommended!',
      ar: 'جعلت المنارة نقل فيلتنا خاليًا تمامًا من التوتر. كان الفريق محترفًا وحريصًا على ممتلكاتنا وأنهى العمل قبل الموعد المحدد. موصى به بشدة!'
    }),
    date: t({ en: 'December 2025', ar: 'ديسمبر 2025' })
  },
  {
    name: t({ en: 'Mohammed Hassan', ar: 'محمد حسن' }),
    location: t({ en: 'Andalous, Kuwait', ar: 'الأندلس، الكويت' }),
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_160622eef-1763294833723.png",
    alt: t({ en: 'Middle-aged Arab businessman in white traditional dress with confident smile', ar: 'رجل أعمال عربي في منتصف العمر يرتدي الزي التقليدي الأبيض بابتسامة واثقة' }),
    rating: 5,
    service: t({ en: 'Commercial Relocation', ar: 'نقل تجاري' }),
    testimonial: t({
      en: 'Our office relocation was handled perfectly. They moved everything over the weekend with zero disruption to our business. Excellent service and fair pricing.',
      ar: 'تم التعامل مع نقل مكتبنا بشكل مثالي. نقلوا كل شيء خلال عطلة نهاية الأسبوع دون أي تعطيل لأعمالنا. خدمة ممتازة وأسعار عادلة.'
    }),
    date: t({ en: 'November 2025', ar: 'نوفمبر 2025' })
  },
  {
    name: t({ en: 'Fatima Al-Rashid', ar: 'فاطمة الراشد' }),
    location: t({ en: 'Khaitan, Kuwait', ar: 'خيطان، الكويت' }),
    image: "https://images.unsplash.com/photo-1657781069918-7aba90d68b62",
    alt: t({ en: 'Young Arab woman with hijab smiling brightly in outdoor setting', ar: 'امرأة عربية شابة بالحجاب تبتسم بشكل مشرق في بيئة خارجية' }),
    rating: 5,
    service: t({ en: 'Packing Service', ar: 'خدمة التعبئة' }),
    testimonial: t({
      en: 'The packing service was worth every fils! They packed everything so carefully, and nothing was damaged. Made moving day so much easier for our family.',
      ar: 'كانت خدمة التعبئة تستحق كل فلس! قاموا بتعبئة كل شيء بعناية فائقة ولم يتضرر أي شيء. جعلت يوم النقل أسهل بكثير لعائلتنا.'
    }),
    date: t({ en: 'January 2026', ar: 'يناير 2026' })
  }];


  const filteredServices =
  activeTab === 'all' ?
  services :
  services.filter((service) => service.id === activeTab);

  const handleQuoteClick = () => {
    router.push('/instant-quote');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-secondary to-brand-ocean text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold mb-6">
              {t({ en: 'Professional Moving Services', ar: 'خدمات نقل احترافية' })}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8">
              {t({ en: 'Comprehensive solutions for every type of move in Kuwait', ar: 'حلول شاملة لكل أنواع النقل في الكويت' })}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:69911181"
                className="flex items-center space-x-2 px-8 py-4 rounded-lg bg-white text-primary hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft">

                <Icon name="PhoneIcon" size={24} variant="solid" />
                <span className="font-accent">6991 1181</span>
              </a>
              <button
                onClick={handleQuoteClick}
                className="flex items-center space-x-2 px-8 py-4 rounded-lg bg-accent text-accent-foreground hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft">

                <Icon name="CalculatorIcon" size={24} variant="solid" />
                <span>{t({ en: 'Get Instant Quote', ar: 'احصل على عرض فوري' })}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Filter Tabs */}
      <section className="bg-card shadow-md sticky top-16 lg:top-20 z-40">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto py-4 space-x-2 scrollbar-hide">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-semibold whitespace-nowrap transition-all duration-base ${
              activeTab === 'all' ? 'bg-brand-purple text-white' : 'bg-muted text-text-primary hover:bg-muted/80'}`
              }>

              <Icon name="Squares2X2Icon" size={20} variant="solid" />
              <span>{t({ en: 'All Services', ar: 'جميع الخدمات' })}</span>
            </button>
            <button
              onClick={() => setActiveTab('residential')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-semibold whitespace-nowrap transition-all duration-base ${
              activeTab === 'residential' ? 'bg-brand-purple text-white' : 'bg-muted text-text-primary hover:bg-muted/80'}`
              }>

              <Icon name="HomeIcon" size={20} variant="solid" />
              <span>{t({ en: 'Residential', ar: 'سكني' })}</span>
            </button>
            <button
              onClick={() => setActiveTab('commercial')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-semibold whitespace-nowrap transition-all duration-base ${
              activeTab === 'commercial' ? 'bg-brand-purple text-white' : 'bg-muted text-text-primary hover:bg-muted/80'}`
              }>

              <Icon name="BuildingOfficeIcon" size={20} variant="solid" />
              <span>{t({ en: 'Commercial', ar: 'تجاري' })}</span>
            </button>
            <button
              onClick={() => setActiveTab('packing')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-semibold whitespace-nowrap transition-all duration-base ${
              activeTab === 'packing' ? 'bg-brand-purple text-white' : 'bg-muted text-text-primary hover:bg-muted/80'}`
              }>

              <Icon name="ArchiveBoxIcon" size={20} variant="solid" />
              <span>{t({ en: 'Packing', ar: 'تعبئة' })}</span>
            </button>
            <button
              onClick={() => setActiveTab('assembly')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-semibold whitespace-nowrap transition-all duration-base ${
              activeTab === 'assembly' ? 'bg-brand-purple text-white' : 'bg-muted text-text-primary hover:bg-muted/80'}`
              }>

              <Icon name="WrenchScrewdriverIcon" size={20} variant="solid" />
              <span>{t({ en: 'Assembly', ar: 'تجميع' })}</span>
            </button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredServices.map((service) =>
            <ServiceCard
              key={service.id}
              {...service}
              onQuoteClick={handleQuoteClick} />

            )}
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <PricingCalculator />
          </div>
        </div>
      </section>

      {/* Service Comparison */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <ServiceComparison />
        </div>
      </section>

      {/* Coverage Map */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <CoverageMap />
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-primary mb-4">
              {t({ en: 'What Our Customers Say', ar: 'ماذا يقول عملاؤنا' })}
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              {t({ en: 'Real experiences from satisfied customers across Kuwait', ar: 'تجارب حقيقية من عملاء راضين في جميع أنحاء الكويت' })}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) =>
            <TestimonialCard key={`${testimonial.name}-${testimonial.location}`} {...testimonial} />
            )}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gradient-to-br from-primary to-secondary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-headline font-bold mb-12">
              {t({ en: 'Why Choose Almanara Moving?', ar: 'لماذا تختار المنارة للنقل؟' })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
                  <Icon
                    name="ShieldCheckIcon"
                    size={32}
                    variant="solid"
                    className="text-white" />

                </div>
                <h3 className="text-xl font-headline font-bold mb-2">
                  {t({ en: 'Fully Insured', ar: 'مؤمن بالكامل' })}
                </h3>
                <p className="text-white/80">
                  {t({ en: 'Complete insurance coverage for peace of mind', ar: 'تغطية تأمينية كاملة لراحة البال' })}
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
                  <Icon
                    name="ClockIcon"
                    size={32}
                    variant="solid"
                    className="text-white" />

                </div>
                <h3 className="text-xl font-headline font-bold mb-2">
                  {t({ en: '24/7 Available', ar: 'متاح على مدار الساعة' })}
                </h3>
                <p className="text-white/80">
                  {t({ en: 'Round-the-clock service for your convenience', ar: 'خدمة على مدار الساعة لراحتك' })}
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
                  <Icon
                    name="CurrencyDollarIcon"
                    size={32}
                    variant="solid"
                    className="text-white" />

                </div>
                <h3 className="text-xl font-headline font-bold mb-2">
                  {t({ en: 'Transparent Pricing', ar: 'أسعار شفافة' })}
                </h3>
                <p className="text-white/80">
                  {t({ en: 'No hidden fees, clear upfront quotes', ar: 'لا رسوم خفية، عروض أسعار واضحة مقدمًا' })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-primary mb-6">
              {t({ en: 'Ready to Move with Confidence?', ar: 'مستعد للانتقال بثقة؟' })}
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              {t({ en: 'Get your free instant quote or speak with our moving experts today', ar: 'احصل على عرض الأسعار الفوري المجاني أو تحدث مع خبراء النقل لدينا اليوم' })}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleQuoteClick}
                className="flex items-center space-x-2 px-8 py-4 rounded-lg bg-brand-purple text-white hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft">

                <Icon name="CalculatorIcon" size={24} variant="solid" />
                <span>{t({ en: 'Get Instant Quote', ar: 'احصل على عرض فوري' })}</span>
              </button>
              <a
                href="tel:69911181"
                className="flex items-center space-x-2 px-8 py-4 rounded-lg bg-secondary text-secondary-foreground hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft">

                <Icon name="PhoneIcon" size={24} variant="solid" />
                <span className="font-accent">{t({ en: 'Call 6991 1181', ar: 'اتصل 6991 1181' })}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default ServicesInteractive;